import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import { useLiveInboundCampaigns } from '../api';
import { buildInboundColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import type { InboundCampaignRow } from '../types';

export function InboundCampaignLivePage() {
  const [campaignName, setCampaignName] = useState('');
  const [did, setDid] = useState('');
  const [appliedFilter, setAppliedFilter] = useState({ campaignName: '', did: '' });

  const query = useLiveInboundCampaigns(appliedFilter);
  /*
   * Legacy backend returns every inbound campaign on this endpoint. We trim
   * to two buckets so the "Live" page actually means "live":
   *   1. Anything not in a terminal state (Running / Paused / Ready / etc.)
   *   2. Terminal-state rows whose endTime falls on today's date — so a
   *      campaign that completed earlier today still shows up here for the
   *      rest of the day. Older completed rows belong on Historical.
   *
   * Terminal status codes (mirrors STATUS_MAP):
   *   2  = Complete, 4/5 = Error, 7 = Cancelled,
   *   11 = System Complete, 12 = System Cancelled
   */
  const TERMINAL_STATUSES = new Set([2, 4, 5, 7, 11, 12]);
  const rows = useMemo(() => {
    // YYYY-MM-DD prefix of "now", matched against the `YYYY-MM-DD HH:MM:SS`
    // wire format used on `endTime` / `startTime`.
    const todayPrefix = (() => {
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    })();
    return (query.data ?? []).filter((r) => {
      const status = Number(r.campaignStatus);
      if (!TERMINAL_STATUSES.has(status)) return true;
      // Terminal row — keep only if it ended today.
      const stamp = r.endTime ?? r.startTime ?? '';
      return stamp.startsWith(todayPrefix);
    });
  }, [query.data]);
  const columns = useMemo(() => buildInboundColumns(), []);

  const handleSearch = () => {
    setAppliedFilter({ campaignName: campaignName.trim(), did: did.trim() });
  };

  return (
    <FillPage>
      <PageHeader
        title="Live Inbound"
        description="Inbound campaigns active on your account. Refreshes every 30 seconds."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              aria-label="Refresh"
            >
              <RefreshCw
                className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
              />
            </Button>
            <Button asChild>
              <Link to="/add-inbound-campaign">
                <Plus className="h-4 w-4" />
                New Inbound Campaign
              </Link>
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Filter by campaign name or by DID (the inbound phone number).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="campaign-name">Campaign name</Label>
              <Input
                id="campaign-name"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="did">DID</Label>
              <Input
                id="did"
                value={did}
                onChange={(e) => setDid(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<InboundCampaignRow, unknown>
        tableId="campaigns:inbound:live:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search campaigns…"
        emptyTitle="No inbound campaigns"
        emptyDescription="Add a new inbound campaign to start receiving calls."
        getRowId={(row) => String(row.campaignId)}
        initialColumnPinning={{ right: ['actions'] }}
        initialColumnVisibility={DEFAULT_VISIBLE_COLUMNS}
        exportFilename="inbound-campaigns-live.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
